import { watch } from 'vue'
import { type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { useAuth } from '../composables/src/useAuth'
import { useAccountStore } from '../stores/account'
import { useCompanyStore } from '../stores/companies'
import { useQueuesStore } from '../stores/queues'
import { useTeamStore } from '../stores/teams'
import { useTicketStore } from '../stores/tickets'
import { useWorkflowStore } from '../stores/workflows'

export async function authGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  if (!to.meta.requiresAuth) {
    return next()
  }

  const auth = useAuth()
  const accountStore = useAccountStore()
  const companyStore = useCompanyStore()
  const teamStore = useTeamStore()
  const ticketStore = useTicketStore()
  const workflowStore = useWorkflowStore()
  const queuesStore = useQueuesStore()

  if (auth.isLoading.value) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(auth.isLoading, (loading) => {
        if (!loading) {
          unwatch()
          resolve()
        }
      })
    })
  }

  if (!auth.isAuthenticated.value) {
    auth.login()

    return
  } else {
    if (!accountStore.user.id) {
      await Promise.allSettled([
        accountStore.setUser(),
        teamStore.fetchTeams(),
        ticketStore.fetchSettings(),
        companyStore.fetchCompanies(false),
        workflowStore.loadAllWorkflowConfigs(),
        queuesStore.fetchQueues(),
      ])
    }

    if ((from.path === '/' || to.name === 'Root') && to.name === 'Root') {
      const defaultQueue = queuesStore.queues.find((q) => q.default === true)

      if (defaultQueue) {
        return next({ path: `/queues/${defaultQueue.slug}` })
      } else {
        return next({ path: '/tickets' })
      }
    }

    if (from.path === '/' && to.path === '/tickets') {
      const defaultQueue = queuesStore.queues.find((q) => q.default === true)

      if (defaultQueue) {
        return next({ path: `/queues/${defaultQueue.slug}` })
      }
    }
  }
  next()
}
